import api from "../../index";

// Types
interface Domain {
  _id: string;
  domainName: string;
  description?: string;
  status: "active" | "inactive";
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  updatedBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

interface DomainCreateInput {
  domainName: string;
  description?: string;
  status?: "active" | "inactive";
}

interface DomainUpdateInput {
  domainName?: string;
  description?: string;
  status?: "active" | "inactive";
}

interface PaginatedResponse {
  domains: Domain[];
  paginationData: {
    page: number;
    perPage: number;
    totalDomains: number;
    totalPages: number;
  };
}

// --------- Domain API --------- //

// Create a domain
export const createDomain = async (values: DomainCreateInput) => {
  return await api.post<Domain>("/domain", values);
};

// Get all domains
export const getAllDomains = async () => {
  return await api.get<Domain[]>("/domain");
};

// Get all domains paginated
export const getDomainsPaginated = async (
  page: number,
  perPage: number,
  searchText?: string
) => {
  const searchParams = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
    ...(searchText && { searchText }),
  });

  return await api.get<PaginatedResponse>(
    `/domain-paginated?${searchParams.toString()}`
  );
};

// Get active domains
export const getActiveDomains = async () => {
  return await api.get<Domain[]>("/domain-active");
};

// Get a domain by ID
export const getDomainById = async (id: string) => {
  return await api.get<Domain>(`/domain/${id}`);
};

// Update a domain by ID
export const updateDomainById = async (
  id: string,
  values: DomainUpdateInput
) => {
  return await api.put<Domain>(`/domain/${id}`, values);
};

// Delete a domain by ID
export const deleteDomainById = async (id: string) => {
  return await api.delete(`/domain/${id}`);
};

// Update domain status
export const updateDomainStatus = async (
  id: string,
  status: "active" | "inactive"
) => {
  return await api.put<Domain>(`/domain/${id}/status`, { status });
};

// Type guard to check if response is paginated
export const isPaginatedResponse = (
  response: any
): response is PaginatedResponse => {
  return (
    response &&
    Array.isArray(response.domains) &&
    response.paginationData &&
    typeof response.paginationData.page === "number" &&
    typeof response.paginationData.perPage === "number" &&
    typeof response.paginationData.totalDomains === "number" &&
    typeof response.paginationData.totalPages === "number"
  );
};

// Error type
export interface ApiError {
  message: string;
  status: string;
}
